import { type ReactNode, useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Star } from 'lucide-react'
import { VerifiedBadge } from '@/components/VerifiedBadge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { AdminOrganization } from '@/types'

type Props = {
  organizations: AdminOrganization[]
  filters: { search: string; plan: string; verified: string }
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function AdminOrganizationsIndex({ organizations, filters }: Props) {
  const [search, setSearch] = useState(filters.search)

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    router.get('/admin/organizations', { search, plan: filters.plan, verified: filters.verified }, { preserveState: true })
  }

  function setFilter(key: string, value: string) {
    const params: Record<string, string> = { search, plan: filters.plan, verified: filters.verified }
    params[key] = params[key] === value ? '' : value
    router.get('/admin/organizations', params, { preserveState: true })
  }

  return (
    <>
      <Head title="Admin - Organizations" />
      <div>
        <h1 className="text-3xl font-semibold tracking-tight font-serif">Organizations</h1>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <form onSubmit={handleSearch} className="flex-1 min-w-[200px] max-w-sm">
            <Input
              placeholder="Search by name or email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </form>
          <div className="flex gap-2">
            <button onClick={() => setFilter('plan', 'pro')}>
              <Badge variant={filters.plan === 'pro' ? 'default' : 'outline'}>Pro</Badge>
            </button>
            <button onClick={() => setFilter('plan', 'free')}>
              <Badge variant={filters.plan === 'free' ? 'default' : 'outline'}>Free</Badge>
            </button>
            <button onClick={() => setFilter('verified', 'true')}>
              <Badge variant={filters.verified === 'true' ? 'default' : 'outline'}>Verified</Badge>
            </button>
            <button onClick={() => setFilter('verified', 'false')}>
              <Badge variant={filters.verified === 'false' ? 'default' : 'outline'}>Unverified</Badge>
            </button>
          </div>
        </div>

        <Table className="mt-6">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Verified</TableHead>
              <TableHead className="text-right">Events</TableHead>
              <TableHead className="text-right">Followers</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {organizations.map(org => (
              <TableRow key={org.id}>
                <TableCell>
                  <Link href={`/admin/organizations/${org.id}`} className="font-medium text-primary hover:underline inline-flex items-center gap-1.5">
                    {org.name}
                    {org.featured && <Star className="size-3.5 fill-primary text-primary" />}
                  </Link>
                </TableCell>
                <TableCell className="text-muted-foreground">{org.email}</TableCell>
                <TableCell>
                  <Badge variant={org.pro ? 'default' : 'secondary'}>{org.pro ? 'Pro' : 'Free'}</Badge>
                </TableCell>
                <TableCell>
                  {org.verified ? (
                    <VerifiedBadge variant="pill" />
                  ) : (
                    <Badge variant="outline" className="text-muted-foreground">Unverified</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">{org.events_count}</TableCell>
                <TableCell className="text-right">{org.followers_count}</TableCell>
                <TableCell className="text-muted-foreground">{formatDate(org.created_at)}</TableCell>
              </TableRow>
            ))}
            {organizations.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  No organizations found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </>
  )
}

AdminOrganizationsIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
